import React, { useState, useEffect } from 'react';
import PrimaryButton from '../../Common/PrimaryButton';

/**
 * Formulário para criar ou editar histórico familiar
 */
const HistoricoFamiliarForm = ({ onClose, onSubmit, initialData = {} }) => {
    const isEditing = !!initialData.id;
    const [formData, setFormData] = useState({
        parentesco: '',
        condicao_medica: '',
        idade_diagnostico: '',
        nome_familiar: '',
        status_vida: '',
        observacoes: ''
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                parentesco: initialData.parentesco || '',
                condicao_medica: initialData.condicao_medica || '',
                idade_diagnostico: initialData.idade_diagnostico || '',
                nome_familiar: initialData.nome_familiar || '',
                status_vida: initialData.status_vida || '',
                observacoes: initialData.observacoes || ''
            });
        }
        setFormError(null);
    }, [initialData, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.parentesco.trim()) {
            setFormError('O parentesco é obrigatório.');
            return;
        }

        if (!formData.condicao_medica.trim()) {
            setFormError('A condição médica é obrigatória.');
            return;
        }

        if (formData.idade_diagnostico && (formData.idade_diagnostico < 0 || formData.idade_diagnostico > 150)) {
            setFormError('A idade do diagnóstico deve estar entre 0 e 150 anos.');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            setFormError(error?.response?.data?.message || error?.message || 'Erro ao salvar histórico familiar.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm" role="alert">
                    {formError}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="parentesco" className="block text-sm font-medium text-gray-700 mb-1">
                        Parentesco <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="parentesco"
                        name="parentesco"
                        value={formData.parentesco}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        required
                    >
                        <option value="">Selecione...</option>
                        <option value="Pai">Pai</option>
                        <option value="Mãe">Mãe</option>
                        <option value="Avô">Avô</option>
                        <option value="Avó">Avó</option>
                        <option value="Irmão">Irmão</option>
                        <option value="Irmã">Irmã</option>
                        <option value="Tio">Tio</option>
                        <option value="Tia">Tia</option>
                        <option value="Primo">Primo</option>
                        <option value="Prima">Prima</option>
                        <option value="Outro">Outro</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="nome_familiar" className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Familiar
                    </label>
                    <input
                        type="text"
                        id="nome_familiar"
                        name="nome_familiar"
                        value={formData.nome_familiar}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Nome do familiar (opcional)..."
                    />
                </div>
            </div>

            <div>
                <label htmlFor="condicao_medica" className="block text-sm font-medium text-gray-700 mb-1">
                    Condição Médica <span className="text-red-500">*</span>
                </label>
                <textarea
                    id="condicao_medica"
                    name="condicao_medica"
                    value={formData.condicao_medica}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Descreva a condição médica do familiar..."
                    required
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="idade_diagnostico" className="block text-sm font-medium text-gray-700 mb-1">
                        Idade no Diagnóstico
                    </label>
                    <input
                        type="number"
                        id="idade_diagnostico"
                        name="idade_diagnostico"
                        value={formData.idade_diagnostico}
                        onChange={handleChange}
                        min="0"
                        max="150"
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Idade em anos..."
                    />
                </div>

                <div>
                    <label htmlFor="status_vida" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                    </label>
                    <select
                        id="status_vida"
                        name="status_vida"
                        value={formData.status_vida}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    >
                        <option value="">Selecione...</option>
                        <option value="vivo">Vivo</option>
                        <option value="falecido">Falecido</option>
                        <option value="desconhecido">Desconhecido</option>
                    </select>
                </div>
            </div>

            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default HistoricoFamiliarForm;

